import { ChevronDown, X } from "lucide-react";
import { Fragment, useState } from "react";

import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface SearchableSelectOption {
  value: string;
  label: string;
  hint?: string;
  /** Options arrive sorted by group; a header shows where each group starts. */
  group?: string;
}

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = "Pilih...",
  emptyText = "Tidak ditemukan.",
  disabled = false,
  clearable = true,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  emptyText?: string;
  disabled?: boolean;
  clearable?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = options.find((o) => o.value === value);
  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  return (
    <Popover
      open={open && !disabled}
      onOpenChange={(next) => {
        if (disabled) return;
        setOpen(next);
        if (!next) setQuery("");
      }}
    >
      <PopoverAnchor asChild>
        <div className="relative w-full">
          <Input
            type="text"
            disabled={disabled}
            value={open ? query : (selected?.label ?? "")}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => !disabled && setOpen(true)}
            onClick={() => !disabled && setOpen(true)}
            placeholder={placeholder}
            autoComplete="off"
            className={cn(
              "w-full cursor-pointer",
              clearable && Boolean(value || query) ? "pr-14" : "pr-8",
              className,
            )}
          />
          <div className="absolute inset-y-0 right-2 flex items-center gap-1 text-muted-foreground">
            {clearable && Boolean(value || query) && !disabled && (
              <button
                type="button"
                tabIndex={-1}
                aria-label="Hapus pilihan"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange("");
                  setQuery("");
                  setOpen(false);
                }}
                className="rounded-sm p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            <ChevronDown className="h-4 w-4 opacity-50 pointer-events-none" />
          </div>
        </div>
      </PopoverAnchor>
      <PopoverContent
        className="z-1200 w-[var(--radix-popover-trigger-width)] min-w-[200px] p-1"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div
          className="max-h-56 overflow-y-auto"
          onWheel={(e) => {
            e.currentTarget.scrollTop += e.deltaY;
          }}
        >
          {filtered.length === 0 ? (
            <p className="px-2 py-1.5 text-sm text-muted-foreground">
              {emptyText}
            </p>
          ) : (
            filtered.map((o, i) => (
              <Fragment key={o.value}>
                {o.group && o.group !== filtered[i - 1]?.group && (
                  <p className="px-2 pt-2 pb-1 text-xs font-semibold text-muted-foreground">
                    {o.group}
                  </p>
                )}
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    if (clearable && o.value === value) {
                      onChange("");
                    } else {
                      onChange(o.value);
                    }
                    setOpen(false);
                    setQuery("");
                  }}
                  className={cn(
                    "flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent",
                    o.value === value && "bg-accent font-medium",
                  )}
                >
                  <span>{o.label}</span>
                  {o.hint && (
                    <span className="text-xs text-muted-foreground">
                      {o.hint}
                    </span>
                  )}
                </button>
              </Fragment>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
